import type { JSX } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import type { LookalikeSeedCounts } from "../api/types";

/** Older discovery builds have no seed-count route and cap analysis at 40. */
export const LEGACY_MAX_SEEDS = 40;

/**
 * Rough wall-clock for an analysis: ~3s a call, 4 at once up to 40 seeds, 8
 * above. Measured: 456 seeds took 3 minutes.
 */
export function estimateDuration(seeds: number): string {
  const seconds = (seeds * 3.2) / (seeds > 40 ? 8 : 4);
  if (seconds < 75) return "about a minute";
  return `about ${Math.round(seconds / 60)} minutes`;
}

/**
 * Step one of a lookalike search: how many of the folder's creators to read.
 *
 * Each seed is one RapidAPI call, so this is priced before anything is spent.
 * Proven creators (invited or joined) are always read first; the rest are
 * spread across the follower range rather than taken biggest-first.
 */
export function LookalikeSeedPicker({
  counts,
  loading,
  value,
  onChange,
}: {
  counts: LookalikeSeedCounts | undefined;
  loading: boolean;
  value: number;
  onChange: (n: number) => void;
}): JSX.Element {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Counting this folder's creators…
      </div>
    );
  }

  const eligible = counts?.eligible;
  const ceiling = Math.min(
    eligible ?? LEGACY_MAX_SEEDS,
    counts?.max ?? LEGACY_MAX_SEEDS,
  );
  if (eligible === 0) {
    return (
      <p className="py-8 text-sm text-muted-foreground">
        This folder has no TikTok creators to learn from. Lookalike search reads
        TikTok hashtags, so Instagram, X, official and gossip accounts can't
        seed it.
      </p>
    );
  }

  const presets = [...new Set([15, 40, ceiling])]
    .filter((n) => n <= ceiling)
    .sort((a, b) => a - b);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Lookalike search reads recent posts from this folder's creators and
        finds the hashtags they share.
        {eligible != null && (
          <>
            {" "}
            <strong className="text-foreground">
              {eligible.toLocaleString()}
            </strong>{" "}
            TikTok creator
            {eligible === 1 ? "" : "s"} can be read
            {counts && counts.proven > 0 && (
              <>
                , {counts.proven} already invited or joined (always read first)
              </>
            )}
            .
          </>
        )}
      </p>

      <div className="grid gap-2">
        <span className="text-sm font-medium">Creators to read</span>
        <div className="flex flex-wrap items-center gap-2">
          {presets.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className={cn(
                "h-9 rounded-md border px-3 text-sm transition-colors",
                value === n
                  ? "border-brand/50 bg-brand/10 font-medium"
                  : "border-border hover:bg-accent/50",
              )}
            >
              {n === ceiling && eligible != null && n === eligible
                ? `All ${n.toLocaleString()}`
                : n}
            </button>
          ))}
          <Input
            type="number"
            min={2}
            max={ceiling}
            value={value}
            onChange={(e) =>
              onChange(
                Math.max(
                  2,
                  Math.min(ceiling, Math.floor(Number(e.target.value)) || 2),
                ),
              )
            }
            className="w-24"
            aria-label="Creators to read"
          />
        </div>
        <span className="text-sm text-muted-foreground">
          About {value.toLocaleString()} API calls, plus one per shortlisted
          hashtag. Takes {estimateDuration(value)}. More creators give a truer
          picture of the niche; on a mixed list they also surface fewer, broader
          hashtags.
        </span>
      </div>
    </div>
  );
}
